import { AnimatePresence } from 'framer-motion';
import { useContext, type Dispatch } from 'react';

import { PinningContext } from '../../pinning-module/pinning-context.js';
import { ScreenShell } from '../../ui-module/screen-shell.js';
import { StartScreen } from './start-screen.js';
import { LoadingScreen } from './loading-screen.js';
import { SuccessScreen } from './success-screen.js';
import { BusinessErrorScreen } from './business-error-screen.js';
import { TechnicalErrorScreen } from './technical-error-screen.js';
import { assertNever } from '../state-machine-feature/assert-never.js';
import { useCancelSubmit } from '../submit-feature/use-cancel-submit.js';
import type { CancelMachine } from '../state-machine-feature/use-cancel-machine.js';
import type { CancelState } from '../state-machine-feature/cancel-state.type.js';
import type { CancelAction } from '../state-machine-feature/cancel-action.type.js';
import type { AppointmentInfo } from '../../token-module/appointment-info.interface.js';

function StartScreenWithSubmit(props: {
  readonly appointmentInfo: AppointmentInfo;
  readonly dispatch: Dispatch<CancelAction>;
}): JSX.Element {
  const submitter = useCancelSubmit({
    appointmentInfo: props.appointmentInfo,
    dispatch: props.dispatch,
  });
  return <StartScreen appointmentInfo={props.appointmentInfo} onSubmit={submitter.submit} />;
}

/**
 * Container für den Start-Screen mit angeschlossenem Submit-Hook (DEV-015).
 *
 * Eigene Komponente, weil `useCancelSubmit` einen verifizierten Tenant-Key
 * aus dem `PinningContext` zieht und die Crypto-/API-Sequenz nur sinnvoll
 * läuft, wenn `appointmentInfo` aus dem Token bereits geparst wurde. Vor
 * dem Token-Parsing fehlt der `appointmentInfo`, dann fällt der Container
 * auf einen prop-freien Start-Screen zurück — er funktioniert weiter, der
 * Submit-Button bleibt im Demo-Switcher-Pfad ohne Backend-Wirkung.
 */
function StartScreenContainer(props: {
  readonly appointmentInfo?: AppointmentInfo;
  readonly dispatch: Dispatch<CancelAction>;
}): JSX.Element {
  const { verifiedKey } = useContext(PinningContext);
  if (!props.appointmentInfo || verifiedKey === null) {
    return props.appointmentInfo ? (
      <StartScreen appointmentInfo={props.appointmentInfo} />
    ) : (
      <StartScreen />
    );
  }
  return (
    <StartScreenWithSubmit appointmentInfo={props.appointmentInfo} dispatch={props.dispatch} />
  );
}

/**
 * Mapped den aktuellen `CancelState` auf die zu rendernde Screen-Komponente
 * (ohne Animations-Wrapper). Erschöpfender Switch ohne `default` — fehlt ein
 * Screen-Case, schlägt der Compiler an.
 */
function renderScreen(state: CancelState, dispatch: Dispatch<CancelAction>): JSX.Element {
  switch (state.screen) {
    case 'START':
      return (
        <StartScreenContainer
          {...(state.appointmentInfo ? { appointmentInfo: state.appointmentInfo } : {})}
          dispatch={dispatch}
        />
      );
    case 'LOADING':
      return (
        <LoadingScreen
          {...(state.appointmentInfo ? { appointmentInfo: state.appointmentInfo } : {})}
        />
      );
    case 'SUCCESS':
      return (
        <SuccessScreen
          {...(state.appointmentInfo ? { appointmentInfo: state.appointmentInfo } : {})}
        />
      );
    case 'BUSINESS_ERROR':
      return (
        <BusinessErrorScreen
          reason={state.reason}
          {...(state.appointmentInfo ? { appointmentInfo: state.appointmentInfo } : {})}
        />
      );
    case 'TECHNICAL_ERROR':
      return <TechnicalErrorScreen variant={state.variant} />;
    default:
      return assertNever(state);
  }
}

interface AnimatedScreensProps {
  readonly machine: CancelMachine;
  readonly tenantId: string;
}

/**
 * Wickelt den aktuellen Screen in eine `ScreenShell` und reiht ihn in
 * `<AnimatePresence mode="wait">` ein. Der `motionKey` enthält Screen +
 * TenantId, damit ein Tenant-Wechsel im Mock-Modus eine saubere Re-Animation
 * auslöst.
 */
export function AnimatedScreens(props: AnimatedScreensProps): JSX.Element {
  const { machine, tenantId } = props;
  const motionKey = `${machine.state.screen}-${tenantId}`;
  // `initial={false}` unterdrückt die Eintritts-Animation beim ersten Render.
  // Kein `mode="wait"`, weil rascher State-Wechsel (z. B. Demo-Bar dispatched
  // RESET→LOADING→SUCCESS in einem Click) sonst im jsdom-Test hängt — der neue
  // Screen muss sofort gemountet sein.
  return (
    <AnimatePresence initial={false}>
      <ScreenShell key={motionKey} motionKey={motionKey}>
        {renderScreen(machine.state, machine.dispatch)}
      </ScreenShell>
    </AnimatePresence>
  );
}
